import Vue from 'vue'
import Router from 'vue-router'
import Home from '@/views/Home.vue'
import { SearchType } from '@/common/enums'
import { Permissions } from '@/models/session/session'
import RouterAuth from './router-auth'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home, // Keep synchronous for optimal initial load
    },
    {
      path: '/job/:jobId',
      name: 'job',
      component: () => import('@/views/JobView.vue'),
      props: true,
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
      },
    },
    {
      path: '/job/:jobId/:engineerRequestId?/:engineerRequestType?',
      name: 'jobWithEngineerRequestId',
      component: () => import('@/views/JobView.vue'),
      props: true,
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
      },
    },
    {
      path: '/identity/callback',
      name: 'identityCallback',
      component: () => import('@/views/Callback.vue'),
      meta: {
        isOidcCallback: true,
        isPublic: true,
      },
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/Users.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'Supervisor'],
      },
    },
    {
      path: '/vipCustomer',
      name: 'vipCustomer',
      component: () => import('@/views/VIPCustomerList.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator'],
      },
    },
    {
      path: '/documenttemplate/',
      name: 'documentTemplate',
      component: () => import('@/views/DocumentTemplate.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator'],
      },
    },
    {
      path: '/contractorManagement/',
      name: 'contractorManagement',
      component: () => import('@/views/Contractor.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail', 'EngineerDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'NetworkManager'],
        showSearchBar: true,
        searchType: SearchType.ContractorJobs,
      },
    },
    {
      path: '/contractorManagement/:contractorId/:contractorAppointedDetailId?',
      name: 'contractorManagementWithId',
      props: true,
      component: () => import('@/views/Contractor.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail', 'EngineerDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'NetworkManager'],
        showSearchBar: true,
        searchType: SearchType.ContractorJobs,
      },
    },
    {
      path: '/contractorManagement/:contractorId/:jobId/:contractorAppointedId',
      name: 'contractorManagement', // There's a hardcoded check for this route name, meaning it needs to be a duplicate of the other one.
      component: () => import('@/views/Contractor.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail', 'EngineerDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'NetworkManager'],
        showSearchBar: true,
        searchType: SearchType.ContractorJobs,
      },
    },
    {
      path: '/networkManagerDashboard',
      name: 'networkManagerDashboard',
      component: () => import('@/views/NetworkManagerDashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'NetworkManager'],
      },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/TwilioDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/tradeManagement',
      name: 'tradeManagement',
      component: () => import('@/views/TradeManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator'],
      },
    },
    {
      path: '/clientmanagement',
      name: 'clientManagement',
      component: () => import('@/views/ClientManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'ClientManager'],
        searchType: SearchType.ClientJobs,
      },
    },
    {
      path: '/clientmanagement/financialSummary',
      name: 'clientManagementwithfinancialSummary',
      component: () => import('@/views/ClientManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'ClientManager'],
        searchType: SearchType.ClientJobs,
      },
    },
    {
      // I don't know if this route is actually still needed anymore, but I'm scared to remove it.
      path: '/clientmanagement/:insurerId/:bordereauName',
      name: 'clientManagementwithBordereauDetail',
      component: () => import('@/views/ClientManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'ClientManager'],
        searchType: SearchType.ClientJobs,
      },
    },
    {
      path: '/complainttracker',
      name: 'complaintTracker',
      component: () => import('@/views/ComplaintTracker.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/managerSupervisorDashboard',
      name: 'managerSupervisorDashboard',
      component: () => import('@/views/ManagerSupervisorDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/jobDashboard',
      name: 'jobDashboard',
      component: () => import('@/views/JobDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/unlinkedPhoneCallsDashboard',
      name: 'unlinkedPhoneCallsDashboard',
      component: () => import('@/views/UnlinkedPhoneCallsDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/incidentmanagerdashboard',
      name: 'incidentmanagerdashboard',
      component: () => import('@/views/IncidentManagerDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/undeployedemergencydashboard',
      name: 'undeployedEmergencyDashboard',
      component: () => import('@/views/UndeployedEmergencyDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/thirdPartyDashboard',
      name: 'thirdPartyDashboard',
      component: () => import('@/views/ThirdPartyDashboard.vue'),
      meta: { requiresDashboardAuth: true },
    },
    {
      path: '/outstandingJobDashboard',
      name: 'outstandingJobDashboard',
      component: () => import('@/views/OutstandingJobDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/sIJobDashboard',
      name: 'sIJobDashboard',
      component: () => import('@/views/SIJobDashboard.vue'),
      meta: {
        requiresDashboardAuth: false,
      },
    },
    {
      path: '/queueDashboard',
      name: 'queueDashboard',
      component: () => import('@/views/QueueDashboard.vue'),
      meta: { requiresDashboardAuth: true },
    },
    {
      path: '/engineerRequestDashboard',
      name: 'engineerRequestDashboard',
      component: () => import('@/views/EngineerRequestDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/ctpDashboard',
      name: 'ctpDashboard',
      component: () => import('@/views/CTPDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/contractorPODashboard',
      name: 'contractorPODashboard',
      component: () => import('@/views/ContractorPODashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
      },
    },
    {
      path: '/clientReports',
      name: 'clientReports',
      component: () => import('@/views/ClientReportsDashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'Finance'],
      },
    },
    {
      path: '/clientReports/:insurerId/:bordereauName',
      name: 'clientReportsWithBordereauDetail',
      component: () => import('@/views/ClientReportsDashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'Finance'],
      },
    },
    {
      path: '/clientInvoiceDashboard',
      name: 'clientInvoiceDashboard',
      component: () => import('@/views/ClientInvoiceDashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
      },
    },
    {
      path: '/dynamicDashboard/:dashboardId?',
      name: 'dynamicDashboard',
      component: () => import('@/views/DynamicDashboard.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'Supervisor', 'Finance', 'ClientManager'],
      },
    },
    {
      path: '/missedJobsDashboard',
      name: 'missedJobsDashboard',
      component: () => import('@/views/MissedJobsDashboard.vue'),
      meta: {
        requiresDashboardAuth: true,
      },
    },
    {
      path: '/usJobDashboard',
      name: 'usJobDashboard',
      component: () => import('@/views/USJobDashboard.vue'),
      meta: {
        requiresDashboardAuth: false,
      },
    },
    {
      path: '/clientInvoiceDashboardSI',
      name: 'clientInvoiceDashboardSI',
      component: () => import('@/views/ClientInvoiceDashboardSI.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
      },
    },
    {
      path: '/cookieManagement',
      name: 'cookieManagement',
      component: () => import('@/views/cookies/CookieManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator'],
      },
    },
    {
      path: '/agentsDashboard',
      name: 'agentsDashboard',
      component: () => import('@/views/AgentsDashboard.vue'),
      meta: {
        requiredPermission: Permissions.FixStuckTasks,
      },
    },
    {
      path: '/regionManager',
      name: 'regionManager',
      component: () => import('@/views/RegionManagement.vue'),
      meta: {
        allowedDetailRecordTypes: ['UserDetail', 'EngineerDetail'],
        allowedUserDetailRoleNames: ['SystemAdministrator', 'Administrator', 'NetworkManager'],
      },
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/UnauthorizedView.vue'),
      meta: {
        isPublic: true,
      },
    },
    {
      path: '*',
      name: 'notFound',
      component: () => import('@/views/NotFound.vue'),
      meta: {
        isPublic: true,
      },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  await RouterAuth.BeforeEach(to, from, next)
})

export default router
